/**
 * PatientRail.tsx — the patient portal's SECTION CATALOG (items + icons).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: UX Wave 10 (patient
 * portal basis, 2026-07-22); reduced to a catalog 2026-07-25.
 *
 * WHY: the signed-in patient area now mounts the SAME shared {@link SideNav}
 * rail as the /admin + /clinicians consoles (ui/SideNav — "never fork the rail
 * per role"), so the bespoke patient-rail component + its scoped shell CSS are
 * gone. What remains — and is still owned here — is the patient's section list
 * (`PATIENT_RAIL_ITEMS`) and its outline icons, which {@link
 * PatientPortal} feeds to `SideNav` as its `items`. The icons mirror the staff
 * outline set (24-grid, single-colour stroke) so the two rails read identically.
 */
import type { ReactNode } from 'react';

export type PatientSection = 'dashboard' | 'plan' | 'orders' | 'forms' | 'profile';

export interface PatientRailItem {
  key: PatientSection;
  label: string;
  icon: ReactNode;
}

/** The shared outline-icon frame — 24-grid, single-colour stroke, no fill (mirrors the staff set). */
function OutlineIcon({ children }: { children: ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

/** Dashboard — a home. */
function IconHome() {
  return (
    <OutlineIcon>
      <path d="M4 11l8-6 8 6" />
      <path d="M6 10v9a1 1 0 0 0 1 1h3v-5h4v5h3a1 1 0 0 0 1-1v-9" />
    </OutlineIcon>
  );
}

/** My plan — a capsule with its half-split seam (matches the staff IconPlans). */
function IconPlan() {
  return (
    <OutlineIcon>
      <rect x="4.2" y="9" width="15.6" height="6" rx="3" transform="rotate(-45 12 12)" />
      <path d="M9.5 14.5l5-5" />
    </OutlineIcon>
  );
}

/** My orders — a delivery box. */
function IconOrders() {
  return (
    <OutlineIcon>
      <path d="M3 7l9-4 9 4v10l-9 4-9-4z" />
      <path d="M3 7l9 4 9-4" />
      <path d="M12 11v10" />
    </OutlineIcon>
  );
}

/** My forms — a clipboard (matches the staff IconAudit family). */
function IconForms() {
  return (
    <OutlineIcon>
      <path d="M9 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2h-2" />
      <rect x="9" y="2" width="6" height="4" rx="1" />
      <path d="M8 11h8" />
      <path d="M8 15h5" />
    </OutlineIcon>
  );
}

/** Profile — a single person (matches the staff IconProfile). */
function IconProfile() {
  return (
    <OutlineIcon>
      <circle cx="12" cy="8" r="4" />
      <path d="M4.5 21v-.5a6 6 0 0 1 6-6h3a6 6 0 0 1 6 6v.5" />
    </OutlineIcon>
  );
}

export const PATIENT_RAIL_ITEMS: readonly PatientRailItem[] = [
  { key: 'dashboard', label: 'Dashboard', icon: <IconHome /> },
  { key: 'plan', label: 'My plan', icon: <IconPlan /> },
  { key: 'orders', label: 'My orders', icon: <IconOrders /> },
  { key: 'forms', label: 'My forms', icon: <IconForms /> },
  { key: 'profile', label: 'Profile', icon: <IconProfile /> },
];
